import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Square,
  Avatar,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { plural, t } from '@lingui/core/macro';
import {
  AsyncSelect,
  Select,
  chakraComponents,
  type OptionProps,
  type CSSObjectWithLabel,
} from 'chakra-react-select';
import { skipToken } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { getUserFullName } from '@duke-hq/lib';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import {
  DUKE_HQ_SITE_NAME,
  ProviderUserRoleColorSchemes,
} from '@duke-hq/shared-constants/providers';
import { useDebounceCallback } from 'usehooks-ts';
import { trpcAdmin } from '../../lib/trpcAdmin';

const menuPortalStyles = {
  menuPortal: (base: CSSObjectWithLabel) => ({
    ...base,
    zIndex: 9999,
  }),
};

function siteLabel(site: { name: string; state?: string | null }) {
  return site.state ? `${site.name}, ${site.state}` : site.name;
}

function toSiteOption(site: { id: string; name: string; state?: string | null }) {
  return { value: site.id, label: siteLabel(site) };
}

interface ConsultantOption {
  value: string;
  label: string | undefined;
  fullname: string | undefined;
  email: string | null;
  picture_url: string | null;
  role: string;
}

function ConsultantOptionComponent(props: OptionProps<ConsultantOption>) {
  return (
    <chakraComponents.Option {...props}>
      <Box display="flex" alignItems="center" justifyContent="space-between" w="full">
        <Box display="flex" alignItems="center">
          <Square size="32px">
            <Avatar
              size="sm"
              name={props.data.fullname}
              src={props.data.picture_url ?? undefined}
            />
          </Square>
          <VStack align="flex-start" gap={0} ml={4}>
            <Text fontWeight="medium">{props.data.label}</Text>
            <Text fontSize="sm" color={props.isSelected ? 'fg.inverse-subtle' : 'fg.muted'}>
              {props.data.email}
            </Text>
          </VStack>
        </Box>
        <Box display="flex" alignItems="flex-end">
          <Badge
            borderRadius="full"
            colorScheme={
              (props.data.role && ProviderUserRoleColorSchemes[props.data.role]) ?? 'gray'
            }
            variant="muted"
            textTransform="none"
          >
            {props.data.role?.replace(/_/g, ' ')}
          </Badge>
        </Box>
      </Box>
    </chakraComponents.Option>
  );
}

interface BulkTransferConsultantModalProps {
  isOpen: boolean;
  onClose: () => void;
  consultantId: string;
  providerId: number;
  enrolmentCount: number;
  referralCount: number;
}

export function BulkTransferConsultantModal({
  isOpen,
  onClose,
  consultantId,
  providerId,
  enrolmentCount,
  referralCount,
}: BulkTransferConsultantModalProps) {
  const utils = trpcAdmin.useUtils();
  const toast = useToast();

  const [selectedConsultantId, setSelectedConsultantId] = useState<string | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [siteIdToGrant, setSiteIdToGrant] = useState<string | null>(null);
  const [showSiteBreakdown, setShowSiteBreakdown] = useState(false);
  const [showGrant, setShowGrant] = useState(false);
  const [keepCurrentSites, setKeepCurrentSites] = useState(true);

  const { data: assignmentsBySite, isFetching: isSiteBreakdownLoading } =
    trpcAdmin.providers.user.consultantActiveAssignmentsBySite.useQuery(
      isOpen && (showSiteBreakdown || keepCurrentSites) ? { providerId, consultantId } : skipToken,
    );

  const { data: consultants, isFetching: isConsultantsLoading } =
    trpcAdmin.providers.user.listConsultants.useQuery({ providerId }, { enabled: isOpen });

  const { data: siteAccess, isFetching: isSitesLoading } =
    trpcAdmin.providers.students.listConsultantSiteAccess.useQuery(
      selectedConsultantId != null ? { providerId, consultantId: selectedConsultantId } : skipToken,
    );
  // Duke HQ is a placeholder until a new team member picks their site, so nothing moves there.
  const sites = useMemo(
    () => siteAccess?.filter((site) => site.name !== DUKE_HQ_SITE_NAME),
    [siteAccess],
  );

  const { data: providerSites } = trpcAdmin.providers.locations.listSiteLocations.useQuery(
    { providerId, filters: { available: true } },
    { enabled: isOpen },
  );

  const siteOptions = useMemo(() => (sites ?? []).map(toSiteOption), [sites]);

  const siteToGrantOptions = useMemo(
    () =>
      (providerSites ?? [])
        .filter((site) => !sites?.some((accessibleSite) => accessibleSite.id === site.id))
        .map(toSiteOption),
    [providerSites, sites],
  );

  const consultantOptions: ConsultantOption[] = useMemo(() => {
    if (!consultants) {
      return [];
    }
    return consultants
      .filter((c) => c.id !== consultantId)
      .map((consultant) => ({
        value: consultant.id,
        label: getUserFullName(consultant),
        fullname: getUserFullName(consultant),
        email: consultant.email,
        picture_url: consultant.picture_url,
        role: consultant.role,
      }));
  }, [consultants, consultantId]);

  const selectedConsultantName =
    consultantOptions.find((option) => option.value === selectedConsultantId)?.label ?? '';
  // A consultant with no sites can't take the transfer until they get one.
  const isGrantShown = showGrant || sites?.length === 0;

  // A caseload that keeps its sites needs the new consultant to reach each one. Only known
  // once both lists have loaded. Duke HQ's caseload moves to a site the admin picks instead.
  const missingCurrentSites = useMemo(
    () =>
      sites == null || assignmentsBySite == null
        ? []
        : assignmentsBySite.flatMap((row) =>
            row.siteId != null && !row.isDukeHQ && !sites.some((site) => site.id === row.siteId)
              ? [{ siteId: row.siteId, available: row.available }]
              : [],
          ),
    [assignmentsBySite, sites],
  );
  const dukeHQRow = assignmentsBySite?.find((row) => row.isDukeHQ);
  const isSitePickerShown = !keepCurrentSites || dukeHQRow != null;
  const hasDisabledMissingSite = missingCurrentSites.some((site) => !site.available);
  const missingSitesText = plural(missingCurrentSites.length, {
    one: '# more site',
    other: '# more sites',
  });

  const enrolmentsText = plural(enrolmentCount, {
    one: '# active enrolment',
    other: '# active enrolments',
  });
  const referralsText = plural(referralCount, {
    one: '# active referral',
    other: '# active referrals',
  });
  const caseloadToMove =
    enrolmentCount > 0 && referralCount > 0
      ? t`${enrolmentsText} and ${referralsText}`
      : enrolmentCount > 0
        ? enrolmentsText
        : referralsText;

  const loadConsultantOptions = useDebounceCallback(
    (inputValue: string, callback: (options: ConsultantOption[]) => void) => {
      if (consultantOptions.length > 0) {
        callback(
          consultantOptions.filter(
            (option) =>
              (option.label != null &&
                option.label.toLowerCase().includes(inputValue.toLowerCase())) ||
              (option.email != null &&
                option.email.toLowerCase().includes(inputValue.toLowerCase())),
          ),
        );
      } else {
        callback([]);
      }
    },
    500,
  );

  const disableConsultant = trpcAdmin.providers.user.disableConsultant.useMutation({
    onSuccess: () => {
      toast({
        title: t`Consultant disabled and assignments transferred successfully.`,
        status: 'success',
        isClosable: true,
      });

      void utils.providers.user.listConsultantsWithPagination.refetch();
      void utils.providers.user.consultantById.refetch();
      void utils.providers.user.consultantActiveAssignmentCounts.invalidate();
      handleClose();
    },
    onError: (error) => {
      toast({
        title: t`Error transferring assignments`,
        description:
          error instanceof Error ? error.message : t`Something went wrong. Please contact support.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // The grant stays even if the admin closes the modal without transferring.
  const createUserSiteAccess = trpcAdmin.providers.locations.createUserSiteAccess.useMutation({
    onSuccess: async (_data, { consultantId: grantedConsultantId, siteId }) => {
      toast({
        title: t`Site access added successfully`,
        status: 'success',
        isClosable: true,
        duration: 3000,
      });

      await Promise.all([
        utils.providers.students.listConsultantSiteAccess.invalidate({
          providerId,
          consultantId: grantedConsultantId,
        }),
        utils.providers.locations.listAccessibleSiteLocations.invalidate(),
        utils.providers.user.listConsultantsWithPagination.invalidate(),
      ]);
      setSelectedSiteId(siteId ?? null);
      setSiteIdToGrant(null);
      setShowGrant(false);
    },
    onError: (error) => {
      toast({
        title: t`Failed to add site access.`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const grantCurrentSites = trpcAdmin.providers.locations.createUserSiteAccess.useMutation({
    onError: (error) => {
      toast({
        title: t`Failed to add site access.`,
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const handleClose = useCallback(() => {
    setSelectedConsultantId(null);
    setSelectedSiteId(null);
    setSiteIdToGrant(null);
    setShowSiteBreakdown(false);
    setShowGrant(false);
    setKeepCurrentSites(true);
    onClose();
  }, [onClose]);

  const grantSiteAccess = useCallback(
    (siteId: string | null) => {
      if (!selectedConsultantId || !siteId) {
        return;
      }
      createUserSiteAccess.mutate({ providerId, consultantId: selectedConsultantId, siteId });
    },
    [selectedConsultantId, providerId, createUserSiteAccess],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedConsultantId) {
      return;
    }
    if (!keepCurrentSites) {
      if (selectedSiteId) {
        disableConsultant.mutate({
          id: consultantId,
          providerId,
          transferTo: { consultantId: selectedConsultantId, siteLocationId: selectedSiteId },
        });
      }
      return;
    }

    if (dukeHQRow != null && selectedSiteId == null) {
      return;
    }

    // One at a time, since each grant can delete the new consultant's Duke HQ access.
    try {
      for (const site of missingCurrentSites) {
        await grantCurrentSites.mutateAsync({
          providerId,
          consultantId: selectedConsultantId,
          siteId: site.siteId,
        });
      }
    } catch {
      // The grant's onError has already told the admin.
      return;
    }
    void utils.providers.students.listConsultantSiteAccess.invalidate({
      providerId,
      consultantId: selectedConsultantId,
    });
    void utils.providers.locations.listAccessibleSiteLocations.invalidate();
    disableConsultant.mutate({
      id: consultantId,
      providerId,
      transferTo: {
        consultantId: selectedConsultantId,
        ...(dukeHQRow != null && selectedSiteId != null
          ? { dukeHQReplacementSiteId: selectedSiteId }
          : {}),
      },
    });
  }, [
    selectedConsultantId,
    keepCurrentSites,
    selectedSiteId,
    disableConsultant,
    consultantId,
    providerId,
    dukeHQRow,
    missingCurrentSites,
    grantCurrentSites,
    utils,
  ]);

  const canSubmit =
    selectedConsultantId != null &&
    (keepCurrentSites
      ? sites != null &&
        assignmentsBySite != null &&
        !hasDisabledMissingSite &&
        (dukeHQRow == null || selectedSiteId != null)
      : selectedSiteId != null);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Trans>Transfer and Disable Consultant</Trans>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <Alert status="warning" alignItems="flex-start" borderRadius="md">
              <AlertIcon />
              <AlertDescription fontSize="sm">
                {keepCurrentSites && dukeHQRow != null ? (
                  <Trans>
                    {caseloadToMove} will move to the consultant you choose. Anything at Duke HQ
                    moves to the site you pick, and the rest stays at its current site. This
                    consultant is then disabled.
                  </Trans>
                ) : keepCurrentSites ? (
                  <Trans>
                    {caseloadToMove} will move to the consultant you choose and stay at their
                    current sites. This consultant is then disabled.
                  </Trans>
                ) : (
                  <Trans>
                    {caseloadToMove} will move to the consultant and site you choose. This
                    consultant is then disabled.
                  </Trans>
                )}
              </AlertDescription>
            </Alert>

            <Box>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setShowSiteBreakdown((shown) => !shown);
                }}
              >
                {showSiteBreakdown ? (
                  <Trans>Hide current sites</Trans>
                ) : (
                  <Trans>Show current sites</Trans>
                )}
              </Button>
              {showSiteBreakdown &&
                (isSiteBreakdownLoading && !assignmentsBySite ? (
                  <Spinner size="sm" display="block" mt={2} />
                ) : (
                  <Table size="sm" mt={2}>
                    <Thead>
                      <Tr>
                        <Th>
                          <Trans>Site</Trans>
                        </Th>
                        <Th isNumeric>
                          <Trans>Enrolments</Trans>
                        </Th>
                        <Th isNumeric>
                          <Trans>Referrals</Trans>
                        </Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {(assignmentsBySite ?? []).map((row) => {
                        const rowSiteLabel =
                          row.siteId == null
                            ? t`No site`
                            : siteLabel({ name: row.name ?? row.siteId, state: row.state });
                        const hasAccess = sites?.some((site) => site.id === row.siteId) === true;
                        // Only once the chosen consultant's sites have loaded, and only for
                        // sites that can still be granted.
                        const canGrant =
                          sites != null &&
                          !hasAccess &&
                          providerSites?.some((site) => site.id === row.siteId) === true;

                        return (
                          <Tr key={row.siteId ?? 'no-site'}>
                            <Td>
                              <Text>{rowSiteLabel}</Text>
                              {hasAccess && (
                                <Badge
                                  mt={1}
                                  colorScheme="green"
                                  variant="subtle"
                                  textTransform="none"
                                  whiteSpace="normal"
                                >
                                  <Trans>{selectedConsultantName} has access</Trans>
                                </Badge>
                              )}
                              {keepCurrentSites && row.isDukeHQ && (
                                <Badge
                                  mt={1}
                                  colorScheme="orange"
                                  variant="subtle"
                                  textTransform="none"
                                  whiteSpace="normal"
                                >
                                  <Trans>Moves to the site you pick</Trans>
                                </Badge>
                              )}
                              {keepCurrentSites &&
                                sites != null &&
                                !hasAccess &&
                                !row.isDukeHQ &&
                                row.siteId != null && (
                                  <Badge
                                    mt={1}
                                    colorScheme={row.available ? 'orange' : 'red'}
                                    variant="subtle"
                                    textTransform="none"
                                    whiteSpace="normal"
                                  >
                                    {row.available ? (
                                      <Trans>
                                        {selectedConsultantName} gets access when you transfer
                                      </Trans>
                                    ) : (
                                      <Trans>Site disabled</Trans>
                                    )}
                                  </Badge>
                                )}
                              {!keepCurrentSites && canGrant && (
                                <Button
                                  mt={1}
                                  size="xs"
                                  variant="outline"
                                  aria-label={t`Give access to ${rowSiteLabel}`}
                                  isLoading={
                                    createUserSiteAccess.isPending &&
                                    createUserSiteAccess.variables?.siteId === row.siteId
                                  }
                                  isDisabled={createUserSiteAccess.isPending}
                                  onClick={() => {
                                    grantSiteAccess(row.siteId);
                                  }}
                                >
                                  <Trans>Give access</Trans>
                                </Button>
                              )}
                            </Td>
                            <Td isNumeric>{row.enrolmentCount}</Td>
                            <Td isNumeric>{row.referralCount}</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                ))}
            </Box>

            <FormControl isRequired>
              <FormLabel>
                <Trans>Transfer to</Trans>
              </FormLabel>
              <AsyncSelect
                placeholder={t`Select consultant`}
                loadOptions={loadConsultantOptions}
                defaultOptions={consultantOptions}
                value={consultantOptions.find((o) => o.value === selectedConsultantId) ?? null}
                onChange={(val: ConsultantOption | null) => {
                  setSelectedConsultantId(val?.value ?? null);
                  setSelectedSiteId(null);
                  setSiteIdToGrant(null);
                  setShowGrant(false);
                }}
                components={{ Option: ConsultantOptionComponent }}
                isLoading={isConsultantsLoading}
                isDisabled={createUserSiteAccess.isPending || grantCurrentSites.isPending}
                menuPortalTarget={document.body}
                styles={menuPortalStyles}
              />
              {selectedConsultantId != null && (
                <Link
                  href={`/providers/${providerId}/consultants/${selectedConsultantId}/details`}
                  isExternal
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  mt={2}
                  fontSize="sm"
                >
                  <Trans>Open {selectedConsultantName} in a new tab</Trans>
                  <Icon as={ExternalLink} boxSize={iconSize.xs} />
                </Link>
              )}
            </FormControl>

            <FormControl>
              <Checkbox
                isChecked={keepCurrentSites}
                onChange={(event) => {
                  setKeepCurrentSites(event.target.checked);
                }}
              >
                {dukeHQRow != null && selectedConsultantId != null ? (
                  <Trans>
                    Keep the current sites, except Duke HQ, and give {selectedConsultantName} access
                    to them
                  </Trans>
                ) : dukeHQRow != null ? (
                  <Trans>
                    Keep the current sites, except Duke HQ, and give the new consultant access to
                    them
                  </Trans>
                ) : selectedConsultantId != null ? (
                  <Trans>
                    Keep the current sites and give {selectedConsultantName} access to them
                  </Trans>
                ) : (
                  <Trans>Keep the current sites and give the new consultant access to them</Trans>
                )}
              </Checkbox>
              {keepCurrentSites &&
                sites != null &&
                assignmentsBySite != null &&
                (hasDisabledMissingSite ? (
                  <FormHelperText color="fg.error">
                    <Trans>
                      Some of this caseload is at a disabled site, so {selectedConsultantName} can't
                      be given access to it.
                    </Trans>
                  </FormHelperText>
                ) : missingCurrentSites.length > 0 ? (
                  <FormHelperText>
                    <Trans>
                      {selectedConsultantName} gets access to {missingSitesText} when you transfer.
                    </Trans>
                  </FormHelperText>
                ) : (
                  <FormHelperText>
                    <Trans>{selectedConsultantName} already has access to every site.</Trans>
                  </FormHelperText>
                ))}
            </FormControl>

            {isSitePickerShown && (
              <FormControl isRequired>
                <FormLabel>
                  {keepCurrentSites ? (
                    <Trans>Site for the caseload at Duke HQ</Trans>
                  ) : (
                    <Trans>Site</Trans>
                  )}
                </FormLabel>
                <Select
                  placeholder={t`Select site`}
                  options={siteOptions}
                  value={siteOptions.find((o) => o.value === selectedSiteId) ?? null}
                  onChange={(val) => {
                    setSelectedSiteId(val?.value ?? null);
                  }}
                  isDisabled={selectedConsultantId == null}
                  isLoading={isSitesLoading}
                  noOptionsMessage={({ inputValue }) =>
                    inputValue
                      ? t`No matching sites`
                      : t`No sites yet. Give them access to one below.`
                  }
                  menuPortalTarget={document.body}
                  styles={menuPortalStyles}
                />
                <FormHelperText>
                  {keepCurrentSites ? (
                    <Trans>
                      Duke HQ isn't a real site, so its enrolments and referrals move here.
                      Everything else keeps its site.
                    </Trans>
                  ) : (
                    <Trans>Every enrolment and referral moves to this site.</Trans>
                  )}
                </FormHelperText>
                {selectedConsultantId != null && !isGrantShown && !isSitesLoading && (
                  <Button
                    variant="link"
                    size="sm"
                    mt={2}
                    onClick={() => {
                      setShowGrant(true);
                    }}
                  >
                    <Trans>Need another site? Give {selectedConsultantName} access</Trans>
                  </Button>
                )}
              </FormControl>
            )}

            {isSitePickerShown && selectedConsultantId != null && isGrantShown && (
              <FormControl>
                <FormLabel>
                  <Trans>Give {selectedConsultantName} access to another site</Trans>
                </FormLabel>
                <HStack spacing={2}>
                  <Box flex={1}>
                    <Select
                      placeholder={t`Select another site`}
                      options={siteToGrantOptions}
                      value={siteToGrantOptions.find((o) => o.value === siteIdToGrant) ?? null}
                      onChange={(val) => {
                        setSiteIdToGrant(val?.value ?? null);
                      }}
                      isDisabled={isSitesLoading}
                      menuPortalTarget={document.body}
                      styles={menuPortalStyles}
                    />
                  </Box>
                  <Button
                    onClick={() => {
                      grantSiteAccess(siteIdToGrant);
                    }}
                    isLoading={createUserSiteAccess.isPending}
                    isDisabled={!siteIdToGrant}
                  >
                    <Trans>Give access</Trans>
                  </Button>
                </HStack>
              </FormControl>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing={2}>
            <Button variant="ghost" onClick={handleClose}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              colorScheme="red"
              onClick={() => {
                void handleSubmit();
              }}
              isLoading={disableConsultant.isPending || grantCurrentSites.isPending}
              isDisabled={!canSubmit}
            >
              <Trans>Transfer and Disable</Trans>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
